import { Injectable } from "@angular/core";
import { BehaviorSubject, EMPTY, Observable, ReplaySubject } from "rxjs";

import { GeocodeFeature, GeocodeRequest } from "@mapbox/mapbox-sdk/services/geocoding";
import Geocoding from "@mapbox/mapbox-sdk/services/geocoding";

import { Coords, MotesPaths, PlaceInfo, SearchInputEvent, Mote } from "../../models";
import { AddressInfo, RestService, MoteService } from '@motes-sdk/providers';
import { HttpClient, HttpDownloadProgressEvent, HttpEventType } from '@angular/common/http';
import { PlaceInfoRequestEntry } from '@motes-sdk/api-models/robot';

interface OpenAiCreateCompletionStream {
    choices: { delta: { content: string }}[];
}

export interface PlaceResult extends Omit<google.maps.places.PlaceResult, 'geometry' | 'photos'> {
    geometry: {
        location: { lat: number, lng: number };
    };
    placeId?: string;
    // Augment google type with position in StoryHunt format
    position: Coords,
    photos: {photo_reference: string}[];
    types?: string[];
}

@Injectable({ providedIn: 'root' })
export class GeoSearchService {
    private readonly mapboxGeocoding = Geocoding({ accessToken: MotesPaths.MAPBOX_ACCESS_TOKEN });

    private searchTextSubject: ReplaySubject<string> = new ReplaySubject();
    private searchEventSubject: ReplaySubject<SearchInputEvent> = new ReplaySubject();
    private readonly searchLanguages = ['en', 'da', 'sv'];

    // Queue and cache for placeId checking
    private placeIdCheckQueue: Array<{mote: Mote, query: string, resolve: Function, reject: Function}> = [];
    private placeIdCheckCache = new Set<string>();
    private isProcessingQueue = false;

    constructor(private resetService: RestService, private httpClient: HttpClient, private moteService: MoteService) {
    }

    /**
    * Search for a geo location / place through the MapBox API and 
    * @proximityBias: Optional proximity bias influencing the bias (could be user's current position)
    */
    public async search(searchQuery: string, proximityBias: Coords = null): Promise<GeocodeFeature[]> {
        if(!searchQuery) return [];

        // Create basic mapbox geocoding / 'places' search request
        let req: GeocodeRequest = {
            query: searchQuery,
            mode: "mapbox.places"
        }
        // Add proximity bias to request if given as parameter (OBS: Proximity bias does not seem to work that well - or not at all)
        if(proximityBias && proximityBias.isValid) req.proximity = proximityBias.longLat();

        req.language = this.searchLanguages;
        // Iso 3166 Codes - https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2
        // req.countries = ['DK', 'SE'];
        req.limit = 6;
        req.autocomplete = true;
        req.types = ['address', "district", "locality", "neighborhood", "poi", "poi", "postcode", "region"];

        let result = await this.mapboxGeocoding.forwardGeocode(req).send();
        return result.body.features;
    }

    /**
     * Search for a geo location / place through the Google places API
     * @param query Query to search for a specific place, city, address or search term (like 'attraction')
     * @param proximityBias Optional location to search for places near by a certain location (proximity bias influencing meaning it is not exact)
     * @param radius Search for places in the given radius around the GPS location (in meters) - obs the radius is not fixed, and usually result in results outside the radius
     */
    public async searchPlaces(searchQuery: string = 'attraction', proximityBias: Coords = null, radius?: number, type?: string): Promise<PlaceResult[]> {
        if(!searchQuery) return [];

        // Get authenticated user token
        let authOptions = await this.resetService.getAuthOptions();

        // Construct StroyHunt street view endpoint url (use all the great default values for heading, pitch fov etc.)
        let url = `${MotesPaths.MOTES_REST}/v1/places/search?query=${searchQuery}`;

        // Add optional type filter
        if(type) url += `&type=${type}`;

        // Add proximity bias to request if given as parameter
        if(proximityBias && proximityBias.isValid) url += `&location=${proximityBias.latLong()}`;
        if(radius) url += `&radius=${radius}`;

        let json = await this.httpClient.get<{ results: PlaceResult[] }>(url, { headers: authOptions }).toPromise().catch(err => {
            return { results: [] } as { results: PlaceResult[] };
        });

        const results = json.results.map( place => {
            place.position = new Coords(place.geometry.location.lat, place.geometry.location.lng);
            place.placeId = place.place_id;
            place.types = place.types || [];
            return place;
        });

        return results;
    }

     /**
     * Append information about places to a list of places / locations
     * @param places A list of places / locations to generate information about
     */
    public async generatePlacesInfo(places: PlaceInfoRequestEntry[]): Promise<Observable<PlaceInfo[]>> {
        // Get authenticated user token
        const authOptions = await this.resetService.getAuthOptions();

        // Construct StroyHunt place info stream endpoint url
        const url = `${MotesPaths.MOTES_REST}/v1/robot/actions/generate-place-info-stream`;
        // Open the stream
        const placeListStream = this.httpClient.post(url, { places }, { headers: authOptions, responseType: 'text', observe: 'events', reportProgress: true });

        if(!placeListStream) return EMPTY;
        const streamPlaceInfo = new BehaviorSubject<PlaceInfo[]>(null);

        placeListStream.subscribe( (chunk) => {
            if(!chunk) return;

            // Check if this is a progress stream event
            if(chunk.type != HttpEventType.DownloadProgress) return;
            // Get the partial JSON text
            const text: string = (chunk as HttpDownloadProgressEvent).partialText;
            let result = "";
            // Parse each line in the partial response (according to the server-sent events standard)
            text.split("data: ").forEach( (textLine) => {
                if(!textLine) return;
                // Check if stream is done
                if(textLine.startsWith("[DONE]")) {
                    streamPlaceInfo.complete();
                    return;
                }
                try {
                    // Parse the OpenAI JSON (each line containing a token)
                    const json: OpenAiCreateCompletionStream = JSON.parse(textLine);
                    // Get the delta data (we only request the most likely token so there is always 1 choice)
                    const word = json.choices[0].delta?.content;

                    if(typeof word == 'string') {
                        // Add the word to the partial JSON string
                        result = result + word;
                        // Parse the partial JSON PlaceInfo[] Object
                        const parsed = this.parseInfoResponse(result);
                        // Notify listeners if we have some new usable data to share
                        if(parsed.valid) streamPlaceInfo.next(parsed.json);
                    }
                }
                catch(err) {
                    console.log(`\nCould not parse line: '${textLine}'`, err);
                }
            });
        });
        return streamPlaceInfo.asObservable();
    }

    /**
     * Parse a partial PlaceInfo and repair the stream response when possible.
     */
    private parseInfoResponse(line: string): { json: any, valid: boolean; } {
        // Skip the initial empty object / array (there is not useful data here anyway)
        if(!line || line.endsWith('[') || line.endsWith('[{')) return { json: '', valid: false };
        // End finished strings of array
        if(line.endsWith("\"")) line += "}]";
        // End unfinished strings in array
        const endswithSpecial = /]|}$/.test(line);
        if(line.endsWith('[{"') || !endswithSpecial) line += '"}]';

        try {
            // Try to parse the partial repaired JSON
            const json = JSON.parse(line);
            return { json, valid: true };
        }
        catch {
            return { json: '', valid: false };
        }
    }

    /**
     * Search for a nearby places by type in the Google places API
     * @param type Type of place to search for (like 'tourist_attraction'), supported types: https://developers.google.com/maps/documentation/places/web-service/supported_types
     * @param location Location to search for places near by
     * @param radius Search for places in the given radius around the GPS location (in meters)
     */
    public async searchNearby(type: string = 'tourist_attraction', location: Coords = null, radius: number = 2000): Promise<PlaceResult[]> {
        if(!type) return [];

        // Get authenticated user token
        let authOptions = await this.resetService.getAuthOptions();

        // Construct StroyHunt street view endpoint url (use all the great default values for heading, pitch fov etc.)
        let url = `${MotesPaths.MOTES_REST}/v1/places/nearby?type=${type}&location=${location.latLong()}&radius=${radius}`;

        let json = await this.httpClient.get<{ results: PlaceResult[]; }>(url, { headers: authOptions }).toPromise().catch(err => {
            return { results: [] } as { results: PlaceResult[]; };
        });
        return json?.results;
    }


    /**
    * Lookup GPS coordinate and get address in return
    */
    public async getAddress(location: Coords): Promise<AddressInfo> {
        // Get authenticated user token
        let authOptions = await this.resetService.getAuthOptions();
        // Construct StroyHunt street view endpoint url (use all the great default values for heading, pitch fov etc.)
        let url = `${MotesPaths.MOTES_REST}/v1/places/address?location=${location.latLong()}`;

        let json = await this.httpClient.get<{ results: google.maps.GeocoderResult[] }>(url, { headers: authOptions }).toPromise().catch(err => {
            console.error("Could not get address: ", err);
            return { results: [] } as { results: google.maps.GeocoderResult[] };
        });

        const emptyAddress: AddressInfo = { name: '', address: '', city: '', country: '' };
        if(!json.results?.length) return emptyAddress;

        const addressResult = json.results.find((result) =>
            result.types.includes('street_address') || result.types.includes('route')
        ) || json.results[0];

        const city = this.findAddressComponent(json.results, 'locality')
            || this.findAddressComponent(json.results, 'administrative_area_level_1')
            || '';

        return {
            name: this.getAddressComponent(addressResult, 'route') || city || '',
            address: addressResult?.formatted_address || '',
            city,
            country: this.findAddressComponent(json.results, 'country') || '',
        };
    }

    private getAddressComponent(result: google.maps.GeocoderResult, componentType: string): string {
        return result?.address_components.find((component) => component.types.includes(componentType))?.long_name;
    }

    private findAddressComponent(results: google.maps.GeocoderResult[], componentType: string): string {
        for(const result of results) {
            const component = this.getAddressComponent(result, componentType);
            if(component) return component;
        }
        return '';
    }

    /**
     * Get a list of search results (places) with city names that match the given query
     * @param searchQuery Name of the city to find
     */
    public async searchCity(searchQuery: string): Promise<GeocodeFeature[]> {
        if(!searchQuery) return [];
        // Create basic mapbox geocoding / 'places' search request
        let req: GeocodeRequest = { query: searchQuery, mode: "mapbox.places" };
        req.language = this.searchLanguages;
        req.types = ['place'];

        // Get result and resolve promise with cities
        let result = await this.mapboxGeocoding.forwardGeocode(req).send();
        return result.body.features;
    }

    /**
    * Emit search event to listeners
    */
    public emitSearchEvent(ev: SearchInputEvent): void {
        this.searchEventSubject.next(ev);
    }

    /**
    * Emit search event to listeners
    */
    public watchSearchEvent(): Observable<SearchInputEvent> {
        return this.searchEventSubject.asObservable();
    }

    /**
    * Set input search text/term and notify listners
    * @param text Search term
    */
    public setSearchText(text: string): void {
        this.searchTextSubject.next(text);
    }

    /**
    * Watch search text changes from external services
    */
    public watchSearchText(): Observable<string> {
        return this.searchTextSubject.asObservable();
    }

    /**
     * Check if a place is a POI (Point of Interest) worth saving place_id for
     * Only returns true for tourist attractions and points of interest
     * @param place The place result to check
     * @returns True if the place is a POI worth having a place_id
     */
    public isPOI(place: PlaceResult): boolean {
        if (!place.types || place.types.length === 0) {
            return false;
        }

        return place.types.includes('tourist_attraction') || 
               place.types.includes('point_of_interest');
    }

    public isCloseEnoughToBeSamePlace(place1: Coords, place2: Coords, maxDistanceMeters: number = 1000): boolean {
        return Coords.calcDistance(place1, place2) < maxDistanceMeters;
    }

    /**
     * Queue a placeId check for a mote - prevents duplicate requests and processes one at a time
     * @param mote The mote to check for placeId
     * @param query Optional search query
     * @param force Force recheck even if already checked
     * @returns Promise that resolves when the check is complete
     */
    public async checkPlaceIdForMote(mote: Mote, query: string = null, force: boolean = false): Promise<void> {
        // If already in cache and not forcing, don't check again
        if (this.placeIdCheckCache.has(mote.key) && !force) {
            return Promise.resolve();
        }

        // Remove from cache and clear timestamp if forcing a recheck
        if (force) {
            this.placeIdCheckCache.delete(mote.key);
            mote.placeIdCheckedOn = null;
        }

        // Return a promise that will be resolved when the check is complete
        return new Promise((resolve, reject) => {
            this.placeIdCheckQueue.push({ mote, query, resolve, reject });
            this.processQueue();
        });
    }

    /**
     * Process the placeId check queue one item at a time
     */
    private async processQueue(): Promise<void> {
        if (this.isProcessingQueue || this.placeIdCheckQueue.length === 0) {
            return;
        }

        this.isProcessingQueue = true;

        while (this.placeIdCheckQueue.length > 0) {
            const { mote, query, resolve, reject } = this.placeIdCheckQueue.shift();
            
            try {
                // Add to cache to prevent duplicate requests
                this.placeIdCheckCache.add(mote.key);
                
                const places = await this.searchPlaces(query || mote.title, mote.position);
                
                // Mark that this mote has been checked
                mote.placeIdCheckedOn = Date.now();
                mote.placeIdIsAttraction = false;
                
                if (places.length > 0) {
                    
                    for (const place of places) {
                        const isPOI = this.isPOI(place);
                        const isCloseEnough = this.isCloseEnoughToBeSamePlace(mote.position, place.position);
                        
                        if (isPOI && isCloseEnough) {
                            mote.placeId = place.placeId;
                            mote.placeIdName = place.name;
                            mote.placeIdIsAttraction = true;
                            break; // Use the first POI found that meets criteria
                        }
                    }
                } else {
                    console.error('No places found for story:', mote.key);
                }
                
                // Save the mote to persist the placeIdCheckedOn timestamp
                await this.moteService.saveMote(mote);
                
                resolve();
            } catch (error) {
                console.error('Error checking placeId for story:', mote.key, error);
                reject(error);
            }
        }

        this.isProcessingQueue = false;
    }
}
